import { useEffect, useState } from "react";
import { CONFIG } from "../config";
import { fmt } from "../lib/format";

/**
 * Le baromètre — ce que le produit assume de publier.
 *
 * C'est la page qui se cite. Une méthode publiée ne suffit pas à faire une
 * référence : il faut une adresse stable où trouver des chiffres datés,
 * versionnés, et tirés de mesures réelles. Jusqu'ici, un journaliste ou un
 * formateur qui voulait s'appuyer sur STRATERA n'avait rien à mettre en note de
 * bas de page.
 *
 * **La valeur de cette page tient à ce qu'elle refuse de publier.** Sous le
 * seuil, elle ne montre aucune ligne, et elle dit pourquoi — un baromètre pris
 * une fois en défaut ne se cite plus jamais. Aujourd'hui cela signifie qu'elle
 * ne publie presque rien : c'est l'état réel du produit, et l'écrire vaut mieux
 * que de le maquiller.
 */

interface Ligne {
  groupe: string;
  libelle: string;
  effectif: number;
  median: number;
  partAutomatisable: number | null;
}

/**
 * Une ligne sectorielle — l'axe dirigeant, 25/08/2026.
 *
 * Pas d'indice : un dirigeant ne reçoit pas de score, il reçoit un volume
 * d'heures. La colonne « indice médian » n'a donc pas d'équivalent ici, et lui
 * en fabriquer un ferait comparer deux mesures qui ne mesurent pas la même
 * chose.
 */
interface LigneSecteur {
  secteur: string;
  libelle: string;
  effectif: number;
  medianHeures: number;
  partAutomatisable: number | null;
}

interface Barometre {
  bareme: string;
  seuil: number;
  diagnostics: number | null;
  groupes: Ligne[];
  secteurs?: LigneSecteur[];
  publiable: boolean;
}

export function Barometre() {
  const [donnees, setDonnees] = useState<Barometre | null>(null);
  const [echec, setEchec] = useState(false);

  useEffect(() => {
    let vivant = true;
    fetch(CONFIG.barometreEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: Barometre) => {
        if (vivant) setDonnees(corps);
      })
      .catch(() => {
        if (vivant) setEchec(true);
      });
    return () => {
      vivant = false;
    };
  }, []);

  const aujourdhui = new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">Baromètre STRATERA</div>
        <h1>L'exposition à l'IA, mesurée en Afrique francophone</h1>
        {/*
          Le chapeau dit l'état réel de la page — 02/09/2026.

          Il annonçait « les chiffres ci-dessous […] repris par toute personne
          qui souhaite les citer » **dans tous les cas**, y compris quand rien
          n'atteint le seuil et que la page ne montre que ses deux avis
          d'attente. Le premier paragraphe promettait donc des chiffres que la
          suite reconnaissait ne pas avoir.

          `publiable` est le champ qui répond à cette question — le serveur le
          calcule et le publie depuis toujours, et plus personne ne le lisait
          depuis que chaque bloc décide de son propre affichage. Les deux
          décisions sont distinctes et le restent : le bloc dit s'il a des
          lignes, le chapeau dit si la page a quelque chose à citer.

          Tant que la réponse n'est pas là, le chapeau ne promet rien : il
          décrit la méthode, ce qui est vrai dans les trois états.
        */}
        <p className="lede">
          {donnees !== null && donnees.publiable ? (
            <>
              Les chiffres ci-dessous viennent des diagnostics réellement effectués sur ce site.
              Ils sont publiés tels quels, avec le barème qui les a produits, et repris par toute
              personne qui souhaite les citer.
            </>
          ) : (
            <>
              Cette page publie les chiffres des diagnostics réellement effectués sur ce site, tels
              quels et avec le barème qui les a produits, dès qu'un groupe professionnel ou un
              secteur atteint le seuil en dessous duquel une moyenne ne mesure rien.
            </>
          )}{" "}
          Nous n'enregistrons pas le pays du répondant : le périmètre est celui des personnes qui
          utilisent ce site, pas celui d'un pays en particulier.
        </p>

        {echec && (
          <div className="notice">
            Les chiffres ne sont pas joignables en ce moment. Réessayez plus tard — nous préférons
            ne rien afficher plutôt qu'une valeur dont nous ne répondons pas.
          </div>
        )}

        {donnees === null && !echec && (
          <p className="fine" role="status">
            Chargement des chiffres…
          </p>
        )}

        {donnees !== null && (
          <>
            <div className="block">
              <h2>Ce que nous publions aujourd'hui</h2>
              {/*
                Le tableau paraît s'il a des lignes, pas si la page est
                publiable — 02/09/2026.

                `publiable` est vrai dès qu'**un des deux axes** a franchi le
                seuil : c'est ce qui permet de publier des chiffres réels sans
                attendre que les autres arrivent. Mais ce bloc-ci s'y adossait,
                si bien que le premier secteur à franchir le seuil faisait
                apparaître un tableau de groupes avec ses quatre en-têtes et
                aucune ligne, surmonté d'une phrase qui explique comment le lire.

                Un tableau vide se lit comme une panne — le commentaire de la
                branche voisine le dit déjà, et c'est cette branche-là qu'il
                fallait prendre. Le baromètre est la seule chose qui rende ce
                produit citable ; paraître cassé y coûte plus qu'ailleurs.
              */}
              {donnees.groupes.length > 0 ? (
                <>
                  <p className="sub">
                    Un groupe professionnel paraît dans ce tableau dès qu'il compte{" "}
                    {donnees.seuil} diagnostics. En dessous, il est compté dans le total mais
                    n'a pas de ligne : une distribution de douze personnes ne mesure pas un métier.
                  </p>
                  <div className="tabwrap">
                    <table className="tab compare">
                      <thead>
                        <tr>
                          <th>Groupe professionnel</th>
                          <th>Diagnostics</th>
                          <th>Indice médian</th>
                          <th>Part des heures automatisables</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donnees.groupes.map((ligne) => (
                          <tr key={ligne.groupe}>
                            <td data-col="Groupe">{ligne.libelle}</td>
                            <td data-col="Diagnostics">{fmt(ligne.effectif)}</td>
                            <td data-col="Indice médian">{ligne.median}/100</td>
                            <td data-col="Heures automatisables">
                              {ligne.partAutomatisable === null
                                ? "—"
                                : `${ligne.partAutomatisable} %`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                /*
                  L'état ordinaire d'un baromètre neuf, dit franchement.

                  Un tableau vide sans explication se lit comme une panne ; une
                  courbe inventée en attendant se lit comme un mensonge dès la
                  première vérification. On écrit ce qui est.
                */
                <div className="notice">
                  <b>Aucun groupe professionnel n'atteint encore {donnees.seuil} diagnostics.</b>{" "}
                  Rien n'est donc publié ici — ni distribution, ni moyenne, ni tendance. Ce n'est
                  pas une panne : c'est le seuil en dessous duquel un chiffre ne mesure rien, et
                  nous ne le franchirons pas pour avoir l'air d'exister.
                  {donnees.diagnostics !== null && (
                    <>
                      {" "}
                      {fmt(donnees.diagnostics)} diagnostics ont été réalisés à ce jour, tous
                      groupes confondus.
                    </>
                  )}
                </div>
              )}
            </div>

            {/*
              L'axe dirigeant, quand il a franchi le seuil — 25/08/2026.

              Il s'affiche dans son propre bloc, et non comme des lignes de plus
              dans le tableau au-dessus : un dirigeant ne reçoit pas d'indice, il
              reçoit un volume d'heures. Mettre les deux dans la même colonne
              ferait comparer une médiane sur cent avec des heures par semaine.

              Le bloc disparaît entièrement sous le seuil, sans phrase
              d'excuse : un baromètre qui annonce à chaque visite ce qu'il ne
              publie pas encore finit par ne parler que de lui-même.
            */}
            {donnees.secteurs !== undefined && donnees.secteurs.length > 0 && (
              <div className="block">
                <h2>Ce que déclarent les entreprises</h2>
                <p className="sub">
                  Mesuré auprès de dirigeants qui ont diagnostiqué leur propre entreprise, secteur
                  par secteur. Les heures sont celles qu'ils déclarent sur les postes répétitifs
                  qu'on leur a soumis — pas la totalité du travail de leurs équipes.
                </p>
                <div className="tab-wrap">
                  <table className="tab">
                    <thead>
                      <tr>
                        <th>Secteur</th>
                        <th>Entreprises</th>
                        <th>Heures par semaine (médiane)</th>
                        <th>Part des heures automatisables</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donnees.secteurs.map((ligne) => (
                        <tr key={ligne.secteur}>
                          <td data-col="Secteur">{ligne.libelle}</td>
                          <td data-col="Entreprises">{fmt(ligne.effectif)}</td>
                          <td data-col="Heures par semaine">{ligne.medianHeures} h</td>
                          <td data-col="Heures automatisables">
                            {ligne.partAutomatisable === null
                              ? "—"
                              : `${ligne.partAutomatisable} %`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="fine">
                  Aucun indice n'est publié ici : l'axe dirigeant mesure des heures et ce qu'elles
                  coûtent, pas une exposition sur cent. Les deux tableaux ne se comparent donc pas
                  ligne à ligne.
                </p>
              </div>
            )}

            <div className="block">
              <h2>Comment lire ces chiffres</h2>
              <p>
                L'<b>indice médian</b> est celui du profil qui coupe le groupe en deux. Il dépend
                de la base du groupe, dérivée de l'indice d'exposition professionnelle à l'IA
                générative de l'Organisation internationale du Travail, et des tâches que chaque
                personne a déclarées.
              </p>
              <p>
                La <b>part des heures automatisables</b> est la seule mesure de cette page qui ne se
                déduit d'aucune source existante. Elle rapporte les heures déclarées sur des tâches
                largement réalisables par une IA au total des heures déclarées. Aucune table
                internationale ne la contient, parce qu'elle ne se calcule pas : elle se demande à
                des personnes qui exercent le métier.
              </p>
              <p className="fine">
                Barème {donnees.bareme}. Mode métier uniquement — les diagnostics faits à partir
                d'un CV ne sont jamais mélangés aux autres, les tâches n'y pesant pas de la même
                façon. Ces chiffres reposent sur des déclarations : ils disent ce que les personnes
                interrogées rapportent de leur travail, ce qui est utile et n'est pas une preuve.
                La méthode complète est publiée sur <a href="/methode">la page méthode</a>.
              </p>
            </div>

            <div className="block">
              <h2>Citer le baromètre</h2>
              <p className="fine">
                « Baromètre STRATERA de l'exposition à l'IA, édité par RISEAFRICA, relevé du{" "}
                {aujourdhui}, barème {donnees.bareme}. » Les bases d'exposition sont une adaptation
                de l'indice de l'OIT (Gmyrek et al., ILO Working Paper 140, 2025, CC BY 4.0) ; cette
                adaptation n'est ni approuvée ni validée par l'OIT.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
